import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, NavLink, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Error, Spinner } from '../../common/Error';
import Hero from '../../common/Hero'
import {
  doVerify,
  doRequestVerification,
  selectUser,
} from './authSlice';

export const RequestVerification = () => {
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const { loading, error, success } = useSelector((state) => state.auth.request_verify)
  const dispatch = useDispatch()

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email is invalid'),
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors } 
  } = useForm({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(validationSchema)
  })

  useEffect(() => {
    // redirect user to verification page if send was successful
    if (success) navigate('/verify')
    // redirect authenticated user to profile screen
    if (user) navigate('/rooms')
  }, [navigate, user, success])

  const submitForm = (data) => {
    // transform email string to lowercase to avoid case sensitivity issues in login
    dispatch(doRequestVerification({
      email: data.email.toLowerCase()
    }))
  }

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Hero />
        <Typography component="h1" variant="h6" sx={{ marginBottom: 2 }}>
          Request verification email
        </Typography>
        <Box component='form' noValidate='noValidate' onSubmit={handleSubmit(submitForm)}>
          <Typography component="p" variant="p">
            {`Enter your account email address and we'll send an email to verify your account.`}
          </Typography>
          <TextField
            id='email'
            autoComplete='email'
            name='email'
            {...register('email')}
            required
            fullWidth
            label='Email'
            margin='normal'
            error={errors.email ? true : false}
            helperText={errors.email?.message}
          />
          <LoadingButton
            size='large'
            type='submit'
            fullWidth
            variant='contained'
            loading={loading}
            loadingPosition="start"
            startIcon={<ForwardToInboxIcon />}
            sx={{ mt: 4, mb: 4 }}
          >
            Send email
          </LoadingButton>
        </Box>
        <Link
          component={NavLink}
          to={'/register'}
          variant="body1"
          sx={{
            mt: 2,
            mb: 2
          }}
        >
          No account? Register a new user
        </Link>
      </Box>
    </Container>
  )
}

export const Verify = () => {
  const user = useSelector(selectUser)
  const { loading, error, success } = useSelector((state) => state.auth.verify)
  const dispatch = useDispatch()
  const [ searchParams, setSearchParams ] = useSearchParams() 
  const verificationCode = searchParams.get('verificationCode')
  const verificationToken = searchParams.get('verificationToken') || localStorage.getItem('verification_token')
  console.log('[Verify]', verificationCode, verificationToken)
  const validationSchema = Yup.object().shape({
    verificationCode: Yup.string()
      .required('Verification code is required')
      .matches(/^[0-9]{6}$/, 'Code must be six digits')
  })
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      verificationToken,
      verificationCode: verificationCode || ''
    },
    resolver: yupResolver(validationSchema)
  })
  const navigate = useNavigate()

  useEffect(() => {
    // redirect user to login page if verification was successful
    if (success) navigate('/login')
    // redirect authenticated user to profile screen
    // if (user && user.verified) navigate('/rooms')
  }, [navigate, success])

  const submitForm = (data) => {
    dispatch(doVerify({
      verification_token: data.verificationToken,
      verification_code: parseInt(data.verificationCode)}))
  }

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Hero />
        <Typography component="h1" variant="h6" sx={{ marginBottom: 2 }}>
          Verify your Babel Chat account
        </Typography>
        <Box component='form' noValidate='noValidate' onSubmit={handleSubmit(submitForm)}>
          <Typography component="p" variant="p">
            Enter the verification code provided in the email we sent. Please allow up to 3 minutes to receive the email.
          </Typography>
          <TextField
            id='verification-code'
            autoComplete='one-time-code'
            type='number'
            name='verificationCode'
            {...register('verificationCode')}
            required
            fullWidth
            margin='normal'
            label='Verification Code'
            error={errors.verificationCode ? true : false}
            helperText={errors.verificationCode?.message || 'Your verification code is 6 digits like 123456'}
          />
          <div className='form-group'>
            <input
              type='hidden'
              className='form-input'
              {...register('verificationToken')}
              required
            />
          </div>
          <LoadingButton
            size='large'
            type='submit'
            fullWidth
            variant='contained'
            loading={loading}
            loadingPosition="start"
            startIcon={<VerifiedUserIcon />}
            sx={{ mt: 4, mb: 4 }}
          >
            Verify account
          </LoadingButton>
        </Box>
        <Link
          component={NavLink}
          to={'/request-verification'}
          variant='body1'
          sx={{
            mt: 2,
            mb: 2
          }}
        >
          Resend verification email
        </Link>
        <Link
          component={NavLink}
          to={'/register'}
          variant="body1"
          sx={{
            mt: 2,
            mb: 2
          }}
        >
          Lost? Register a new user
        </Link>
      </Box>
    </Container>
  )
}
